import useAppContext from "@/lib/useAppContext";
import { formatMoney } from "@justhome/common";
import {
  APPLICATION_ID_PARAMETER,
  LEAD_ID_PARAMETER,
  PROJECT_ID_PARAMETER,
} from "@justhome/common/constants";
import {
  useGetBestOffers,
  useGetOffersWithLowestInterestRate,
  useGetOffersWithLowestMonthlyInstallment,
} from "@justhome/common/hooks";
import { Button, Loader, OfferCard, type Locale } from "@justhome/react-ui";
import clsx from "clsx";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../lib/i18n";
import { usePropertyTypeLabel } from "../../lib/usePropertyTypeLabel";
import InterestRateModal from "../InterestRateModal";

const LabelValue: FC<{ label?: string; value?: string }> = ({
  label,
  value,
}) => {
  return (
    <p className="text-body-2 justify-between whitespace-nowrap">
      {label && (
        <span className="text-opacity-60 text-neutral-675">{label}:</span>
      )}
      <span> {value}</span>
    </p>
  );
};

const OffersStep: FC = () => {
  const { t } = useTranslation();
  const propertyTypeLabel = usePropertyTypeLabel();
  const { state } = useAppContext();
  const { lead, application } = state;
  const form = state.form!;
  const offers = application?.offerPreviews;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState<string>();
  const bestOffers = useGetBestOffers(offers);
  const offersWithLowestMonthlyInstallment =
    useGetOffersWithLowestMonthlyInstallment(offers);
  const offersWithLowestInterestRate =
    useGetOffersWithLowestInterestRate(offers);

  const moreOffersHref = `${process.env.PUBLIC_WEBSITE_HOST}/vergleich/offers?${APPLICATION_ID_PARAMETER}=${application?.id}&${LEAD_ID_PARAMETER}=${lead?.id}`;
  const callBookingHref = `${process.env.PUBLIC_WEBSITE_HOST}/partner/${state.partner?.id}/meeting?${PROJECT_ID_PARAMETER}=${lead?.projectId}&${LEAD_ID_PARAMETER}=${lead?.id}`;

  const isLoading = !(application && lead);

  const hasOffers = !isLoading && !!bestOffers?.length;

  const handleOfferClick = (offerId: string) => {
    setSelectedOfferId(offerId);
    setIsModalOpen(true);
  };

  return (
    <div>
      <h1 className="text-h2 mb-s m:mb-2xs text-center">{t("offers.title")}</h1>
      <div className="flex flex-col justify-center m:flex-row gap m:items-end mb-m">
        <div className="gap-2xs m:gap-x-3s grid grid-cols-1  s:grid-cols-[min-content_min-content]">
          <LabelValue
            value={`${propertyTypeLabel[form.propertyType]} in ${
              form.postalCode
            }`}
          />
          <LabelValue
            label={t("offers.purchasePrice")}
            value={formatMoney(form.purchasePrice || 0, false, "de")}
          />
          <LabelValue
            label={t("offers.equity")}
            value={formatMoney(form.equity || 0, false, "de")}
          />
          <LabelValue
            label={t("offers.monthlyNetIncome")}
            value={formatMoney(form.monthlyNetIncome || 0, false, "de")}
          />
        </div>
        <Button
          iconStart="Edit"
          size="sm"
          variant="outline_primary"
          href={moreOffersHref}
          target="_blank"
          className={clsx({ invisible: isLoading })}
        >
          {t("offers.changeDetails")}
        </Button>
      </div>
      {!isLoading && !hasOffers && (
        <div
          data-testid="no-results"
          className="text-center max-w-[720px] px-m py-3s bg-neutral-0 border border-neutral-50 rounded-3xs"
        >
          <h1 className="text-h2 mb-2xs">{t("offers.noResultsTitle")}</h1>
          <p className="mb-3s">{t("offers.noResultsDescription")}</p>
          <div className="flex flex-col m:flex-row gap-3xs justify-center items-center">
            <Button
              iconStart="Edit"
              target="_blank"
              size="md"
              href={moreOffersHref}
              className="flex-1"
            >
              {t("offers.changeDetails")}
            </Button>
            <span className="text-body-2 text-neutral-675 text-opacity-60">
              {t("offers.or")}
            </span>
            <Button
              iconStart="Calendar"
              target="_blank"
              size="md"
              href={`${process.env.PUBLIC_WEBSITE_HOST}/meeting`}
              className="flex-1"
            >
              {t("offers.bookFreeConsultation")}
            </Button>
          </div>
        </div>
      )}
      {isLoading && (
        <div className="grid grid-cols-1 m:grid-cols-3 gap-2xs m:gap w-full mb-3s relative">
          <div className="absolute inset-x-3s top-m flex justify-center items-center">
            <div className="relative px-3s py-m bg-primary-800 rounded-2xs z-10 shadow-1 text-neutral-0 text-center">
              <Loader size="sm" className="absolute top-2xs right-2xs" />

              <p>{t("offers.loadingMessage")}</p>
            </div>
          </div>
          <OfferCard isLoading locale={i18n.language as Locale} />
          <OfferCard isLoading locale={i18n.language as Locale} />
          <OfferCard isLoading locale={i18n.language as Locale} />
        </div>
      )}
      {!isLoading && hasOffers && (
        <>
          <div className="grid grid-cols-1 m:grid-cols-3 gap-2xs m:gap w-full mb-3s">
            {bestOffers?.map((offer) => (
              <OfferCard
                key={offer.id}
                isLoading={false}
                offer={offer}
                locale={i18n.language as Locale}
                addBadgePlaceholder
                buttonLabel={t("offers.reserveInterestRate")}
                onButtonClick={() => handleOfferClick(offer.id!)}
                isLowestMonthlyInstallment={offersWithLowestMonthlyInstallment.has(
                  offer.id!,
                )}
                isLowestInterestRate={offersWithLowestInterestRate.has(
                  offer.id!,
                )}
              />
            ))}
          </div>
          <div className="flex flex-col m:flex-row justify-center items-center">
            <Button
              size="md"
              iconEnd="RightSmall"
              href={moreOffersHref}
              target="_blank"
              variant="ghost"
            >
              {t("offers.discoverMoreOffers")}
            </Button>
            <Button
              size="md"
              iconEnd="RightSmall"
              href={callBookingHref}
              target="_blank"
              variant="ghost"
            >
              {t("offers.bookAppointment")}
            </Button>
          </div>
        </>
      )}
      <InterestRateModal
        lead={lead!}
        isOpen={isModalOpen}
        offerId={selectedOfferId}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default OffersStep;
